import {Ed25519KeyIdentity} from '@dfinity/identity';
import TorusSdk, {SubVerifierDetails} from '@toruslabs/customauth';
import React from 'react';
import useAuthSDK from '../hooks/useAuthSDK';
import useIdentity from '../hooks/useIdentity';
import usePrivateKeyState from '../hooks/usePrivateKeyState';

const AUTH_DOMAIN = 'https://gvmo.us.auth0.com';

const googleInfo: SubVerifierDetails = {
  typeOfLogin: 'google',
  verifier: 'epoch-google',
  clientId: '962313737544-ikp2bgoshin44l24ep3k9fitmv2nv4i5.apps.googleusercontent.com',
};

const linkedinInfo: SubVerifierDetails = {
  typeOfLogin: 'linkedin',
  verifier: 'epoch-clan-auth0-linkedin',
  clientId: 'pfkos5eETfdlymW5O9qdwc3y2JmbKEW7',
  jwtParams: {
    domain: AUTH_DOMAIN,
  },
};

const passwordlessInfo: SubVerifierDetails = {
  typeOfLogin: 'email_password',
  verifier: 'epoch-clan-auth0-email-password',
  clientId: 'pfkos5eETfdlymW5O9qdwc3y2JmbKEW7',
  jwtParams: {domain: AUTH_DOMAIN},
};

type LoginType = 'google' | 'linkedin' | 'passwordless';

type AuthContextType = {
  identity: Ed25519KeyIdentity | undefined;
  login: (type: LoginType, login_hint?: string) => Promise<void>;
  isLoggedIn: boolean;
  authSdk: TorusSdk | undefined;
  privateKey: string;
  isLoading: boolean;
};

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);
AuthContext.displayName = 'AuthContext';

function AuthProvider(props: {children: React.ReactNode}) {
  const authSdk = useAuthSDK();
  const [privateKey, setPrivateKey] = usePrivateKeyState();
  const identity = useIdentity(privateKey);
  const [isLoading, setIsLoading] = React.useState(false);

  const isLoggedIn = !!identity;

  const login = React.useCallback(
    async (type: LoginType, login_hint?: string) => {
      try {
        if (!authSdk) {
          throw new Error('authSdk is not defined');
        }
        setIsLoading(true);
        let loginData = googleInfo;
        if (type === 'linkedin') {
          loginData = linkedinInfo;
        }
        if (type === 'passwordless') {
          loginData = passwordlessInfo;
          if (login_hint) {
            loginData = {
              ...passwordlessInfo,
              jwtParams: {domain: AUTH_DOMAIN, login_hint},
            };
          }
        }
        const loginResult = await authSdk.triggerLogin(loginData);
        setPrivateKey(loginResult.privateKey);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [authSdk, setPrivateKey]
  );

  const value = React.useMemo(() => {
    return {
      identity,
      login,
      isLoggedIn,
      authSdk,
      privateKey,
      isLoading,
    };
  }, [authSdk, identity, isLoading, isLoggedIn, login, privateKey]);

  return <AuthContext.Provider {...props} value={value} />;
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}

export {AuthProvider, useAuth};
